import classNames from "classnames/dedupe";
import React, { FC, ReactNode, useMemo } from "react";
import { deepClone } from "../../../helpers";
import { BREAKPOINTS, TBreakpoint } from "./Column";

export type TJustifyContent =
  | "justify-content-start"
  | "justify-content-center"
  | "justify-content-end"
  | "justify-content-around"
  | "justify-content-between"
  | "justify-content-evenly";

export type TAlignItems =
  | "align-items-start"
  | "align-items-center"
  | "align-items-end";

export type TRowBreakpoints = Record<TBreakpoint, number>;

export interface IRowProps extends Partial<TRowBreakpoints> {
  as?: React.ElementType;
  justifyContent?: TJustifyContent;
  alignItems?: TAlignItems;
  /** Adds gutters to top and bottom */
  vertical?: boolean;
  classes?: {
    root?: string;
  };
  /** Class prefix for rows */
  rowPrefix?: string;
  /** Class prefix for columns */
  columnPrefix?: string;
  children?: ReactNode;
}

const DEFAULT_ROW_PREFIX = "row";
const DEFAULT_COLUMN_PREFIX = "cols";

const Row: FC<IRowProps> = ({
  classes,
  as: Component = "div",
  justifyContent,
  alignItems,
  children,
  vertical,
  rowPrefix = DEFAULT_ROW_PREFIX,
  columnPrefix = DEFAULT_COLUMN_PREFIX,
  ...other
}) => {
  const classNamePrefix = `${rowPrefix}-${columnPrefix}`;

  /** *
   * First filter out 'BREAKPOINTS' from 'props',
   *  then map result array to class names for 'Row'.
   * Removes all 'BREAKPOINTS' from 'props' to pass 'other' into 'Component'
   * Returtn empty arr in case of no 'BREAKPOINTS'
   *  */
  const computedClasses = useMemo(
    () =>
      Object.keys(deepClone(other))
        .filter((key) => BREAKPOINTS.includes(key as any))
        .map((breakpoint) => {
          const columnsCount = other[breakpoint];
          // eslint-disable-next-line no-param-reassign
          delete other[breakpoint];

          const breakpointName = breakpoint !== "xs" ? `-${breakpoint}` : "";

          return `${classNamePrefix}${breakpointName}-${columnsCount}`;
        }) || [],
    [classNamePrefix, other],
  );

  const rootClassName = classNames(
    rowPrefix,
    classes?.root,
    justifyContent,
    alignItems,
    {
      [`${rowPrefix}--vertical`]: vertical,
    },
    ...computedClasses,
  );

  return (
    <Component className={rootClassName} {...other}>
      {children}
    </Component>
  );
};

export default Row;
